import { AnimatePresence, motion } from "framer-motion";
import { Phone, MessageCircle, Check, ArrowRight, Sun } from "lucide-react";
import { toast } from "sonner";
import { useProto } from "../ProtoContext";
import { PEOPLE, TODAY_PLAN } from "../data";
import { Avatar, Card, Eyebrow, Pill, Section, fullName, lastText, nextText } from "../ui";
import { cn } from "@/lib/utils";

export const partOfDay = () => {
  const h = new Date().getHours();
  return h < 12 ? "morning" : h < 17 ? "afternoon" : "evening";
};
const dateLabel = () => new Date().toLocaleDateString("en-US", { weekday: "long", month: "long", day: "numeric" });

export const ContactActions = ({ p, big }) => {
  const { openLog } = useProto();
  const contact = (how) =>
    toast(`${how === "phone" ? "Calling" : "Texting"} ${p.first}…`, {
      description: "In the real app this opens your phone. When you're done, log it here.",
      action: { label: "Log it", onClick: () => openLog(p.id, how) },
    });
  return (
    <div className={cn("grid grid-cols-[auto_auto_1fr] gap-2", big ? "mt-6" : "mt-5")}>
      <button className="btn btn-quiet" onClick={() => contact("phone")} data-testid={`call-${p.id}`}><Phone size={18} /> Call</button>
      <button className="btn btn-quiet" onClick={() => contact("text")} data-testid={`text-${p.id}`}><MessageCircle size={18} /> Text</button>
      <button
        className="btn btn-primary"
        onClick={() => openLog(p.id)}
        data-testid={`log-care-${p.id}`}
      >
        Log care
      </button>
    </div>
  );
};

const AttentionCard = ({ p, i }) => {
  const { openPerson } = useProto();
  return (
    <motion.div layout className="py-7 border-b border-line" initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0, transition: { delay: 0.08 * i } }} exit={{ opacity: 0, x: 60, transition: { duration: 0.3 } }} data-testid={`attention-card-${p.id}`}>
      <div className="thread">
        <button className="flex gap-5 text-left w-full group" onClick={() => openPerson(p.id)} data-testid={`open-person-${p.id}`}>
          <Avatar person={p} size={64} />
          <div className="min-w-0 flex-1">
            <h3 className="t-name-lg group-hover:text-brand transition-colors">{fullName(p)}</h3>
            <p className="t-body text-[17px] mt-1.5">{p.reason}</p>
            <p className="t-meta mt-1">{lastText(p)}</p>
            <Pill tone={p.next.tone} className="mt-3" data-testid={`attention-next-${p.id}`}>{nextText(p)}</Pill>
          </div>
        </button>
        <ContactActions p={p} />
      </div>
    </motion.div>
  );
};

const MomentCard = ({ p }) => {
  const { openPerson, openLog } = useProto();
  return (
    <div className="tint p-5 thread thread-rhythm" style={{ paddingLeft: 26 }} data-testid={`moment-card-${p.id}`}>
      <button className="flex gap-4 text-left w-full" onClick={() => openPerson(p.id)} data-testid={`open-person-moment-${p.id}`}>
        <Avatar person={p} size={52} />
        <div className="min-w-0">
          <p className="t-name">{fullName(p)}</p>
          <p className="t-body mt-1">{p.reason}</p>
          <p className="t-meta mt-0.5">{lastText(p)}</p>
        </div>
      </button>
      <div className="flex flex-wrap items-center justify-between gap-3 mt-4 pl-[68px]">
        <Pill tone="rhythm">{nextText(p)}</Pill>
        <button onClick={() => openLog(p.id)} className="btn btn-outline btn-sm bg-surface" data-testid={`moment-log-${p.id}`}>Log care</button>
      </div>
    </div>
  );
};

const DoneRow = ({ p, info }) => (
  <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} className="flex items-center gap-4 py-3" data-testid={`done-row-${p.id}`}>
    <div className="relative">
      <Avatar person={p} size={44} />
      <span className="absolute -right-1 -bottom-1 h-6 w-6 rounded-full bg-brand text-white flex items-center justify-center ring-2 ring-paper"><Check size={14} strokeWidth={3} /></span>
    </div>
    <div className="min-w-0">
      <p className="t-name-sm">{fullName(p)}</p>
      <p className="t-meta">{info.methodLabel} · {info.nextLabel ? `Next check-in ${info.nextLabel}` : "No follow-up scheduled"}</p>
    </div>
  </motion.div>
);

const Upcoming = ({ p }) => {
  const { openPerson } = useProto();
  return (
    <button onClick={() => openPerson(p.id)} className="w-full flex items-center gap-4 py-4 text-left group" data-testid={`upcoming-${p.id}`}>
      <Avatar person={p} size={44} />
      <div className="flex-1 min-w-0">
        <p className="t-name-sm group-hover:text-brand transition-colors">{fullName(p)}</p>
        <p className="t-meta truncate">{p.reason}</p>
      </div>
      <span className="text-sm font-medium text-ink-soft shrink-0">{p.next.due}</span>
    </button>
  );
};

const PastorNudge = () => {
  const { go } = useProto();
  return (
    <button onClick={() => go("overview")} className="mt-6 w-full flex items-center gap-3 tint px-5 py-4 text-left hover:bg-line transition-colors" data-testid="today-overview-nudge">
      <Sun size={20} className="text-sun shrink-0" />
      <span className="flex-1 text-[15px] text-ink">Across Riverside, <b>3 care needs</b> are waiting for someone to take them.</span>
      <ArrowRight size={18} className="text-ink-faint" />
    </button>
  );
};

export default function Today() {
  const { role, me, completed, isPhone, rhythm } = useProto();
  const plan = TODAY_PLAN[role];
  const pending = plan.attention.filter((id) => !completed[id]);
  const done = Object.keys(completed);
  const n = pending.length;
  return (
    <div className={cn("pb-36", isPhone ? "px-5 pt-8" : "max-w-3xl mx-auto px-10 pt-16")} data-testid="today-screen">
      <Eyebrow>{dateLabel()}</Eyebrow>
      <h1 className="t-greeting mt-3" data-testid="today-greeting">Good {partOfDay()}, {me.first}.</h1>
      <p className="t-lead mt-4" data-testid="today-summary">
        {n ? <>You have <span className="text-ink font-semibold">{n} {n === 1 ? "person" : "people"}</span> who {n === 1 ? "needs" : "need"} your attention today.</> : <>Everyone who needed you today has been cared for. Thank you, {me.first}.</>}
      </p>
      {role === "pastor" && <PastorNudge />}
      <div className="mt-6 border-t border-line">
        <AnimatePresence mode="popLayout">
          {pending.map((id, i) => <AttentionCard key={id} p={PEOPLE[id]} i={i} />)}
        </AnimatePresence>
      </div>
      {plan.moment && !completed[plan.moment] && (
        <Section title="When you have a moment" hint={`Outside your church's ${rhythm}-day care rhythm`}>
          <MomentCard p={PEOPLE[plan.moment]} />
        </Section>
      )}
      {done.length > 0 && (
        <Section title="Cared for today" data-testid="done-section">
          {done.map((id) => <DoneRow key={id} p={PEOPLE[id]} info={completed[id]} />)}
        </Section>
      )}
      {plan.upcoming.length > 0 && (
        <Section title="Coming up this week">
          <div className="rule-list border-y border-line">{plan.upcoming.map((id) => <Upcoming key={id} p={PEOPLE[id]} />)}</div>
        </Section>
      )}
    </div>
  );
}
